/* eslint-disable indent */
import React, { useState, FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { LayoutAnimation, View } from 'react-native';

import { GridBox, fullHeight, fullWidth, scaleOP } from '@components/frame';
import Panel from './components/panel';
import EnterCount from './components/enterCount';
import ColorsWrapper, { WithColor } from '@styles/colors';
import {
  machineStatus as machineStatuses,
  machineDetailsConditions,
  navigateBack,
  isWeb,
  workerType,
  subStatuses,
  safeAreaInsetsBottom,
  safeAreaInsetsTop,
  getMachineBackgroundByState,
} from '../../helpers';

import useStatusBar from '@components/common/useStatusBar';

import { setSelectedMachine, getMachines } from '@reducers/machines';
import { ColorFunction } from '@styles/colors/types';
import { Machine } from '@interfaces/machines';
import { ClampingTool, OperationSearchItem, ProductionBatchStatistics } from '@interfaces/search';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import { loadSingleBatch, correctQuantity } from '@reducers/batch';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { operationsSearch } from '@reducers/search';
import { setJobs } from '@reducers/details';
import { UnavailableContentComponents } from './components/UnavailableContentComponents';
import { LoadSingleBatch, SearchEntity } from '@interfaces/methods';
import { fontOP } from '@styles/fonts';
import { useTrackOperatorPanelOpened } from './hooks/useTrackOperatorPanelOpened';
import { useWaitForTransitionEnd } from '@v2/pages/common/hooks/useWaitForTransitionEnd';
import { TourTarget } from '@components/tours';
import { OPERATOR_PANEL_TOUR_IDS } from '@components/tours/config/operatorPanel/constants';

export interface Connector {
  loaded: boolean;
  connectorDetails: {
    status: {
      lastAliveEventReceivedOn: string;
      workerType: (typeof workerType)[keyof typeof workerType];
      [key: string]: unknown;
    }[];
    [key: string]: unknown;
  };
  oldStatuses: Record<string, unknown>;
  errMsg: null | 'string';
  isPRODUCTIONExist: boolean;
}

interface OperatorPanelProps extends WithColor {
  selectedItem: Machine;
  clampingTools: ClampingTool[];
  setSelectedMachine: (machine: Machine) => void;
  getMachines: (formatResult?: boolean, preselectedId?: number) => Promise<Machine[]>;
  correctQuantity: (data: any) => Promise<any>;
  loadSingleBatch: LoadSingleBatch;
  batch: ReducerEntityWithLoadingState<ProductionBatchStatistics | null>;
  operationsSearch: SearchEntity<OperationSearchItem>;
  setJobs: (jobs: OperationSearchItem[]) => void;
  connector: Connector;
  machines: Machine[];
  machinesLoaded: boolean;
}

const OperatorPanel: FC<OperatorPanelProps> = (props) => {
  const {
    C,
    selectedItem = {} as Machine,
    clampingTools = [],
    setSelectedMachine,
    getMachines,
    loadSingleBatch,
    batch,
    operationsSearch,
    setJobs,
    connector,
    machines,
    machinesLoaded,
    correctQuantity,
  } = props;

  const route = useRoute();
  const queryId = route.params?.id;

  const readyToLoad = useWaitForTransitionEnd();

  useTrackOperatorPanelOpened();

  const isOperator = true;

  const { machineStatus, machineId } = selectedItem;
  const { status, subStatus, ncFileStatus, ncFile } = machineStatus || {};

  const [maxCount, count] = useMemo(() => {
    const maxCount = batch?.data?.links?.reduce((total, l) => total + l.quantityTarget, 0) ?? 0;
    const count = batch?.data?.quantityReady ?? 0;
    return [maxCount, count];
  }, [batch?.data?.links, batch?.data?.quantityReady]);

  // TODO: Add API call
  const setCount = () => {};
  //

  const [showEnterCount, updateShowEnterCount] = useState(false);

  const setShowEnterCount = (v) => {
    LayoutAnimation.easeInEaseOut();
    updateShowEnterCount(v);
  };

  const { machineActive, machineOffline, ignoredNcFile } = machineDetailsConditions({
    status,
    ncFileStatus,
    subStatus,
    ncFileExist: !!ncFile,
  });

  // Function for overwriting colors function,
  // need for disabled changing colors when users change device theme
  const CC = (c: string) => C(c, false);

  const bgColor = CC(getMachineBackgroundByState({ ignoredNcFile, machineActive, machineOffline, status }));

  useStatusBar({
    C,
    background: bgColor,
    style: { light: 'light-content', dark: 'light-content' },
  });

  useFocusEffect(
    useCallback(() => {
      if (!readyToLoad) return;
      if (machineId === queryId) return;
      (async () => {
        const machines = await getMachines(true);
        const machine = machines.find((m) => m.machineId == queryId);
        if (machine) {
          setSelectedMachine(machine);
        } else {
          return navigateBack();
        }
      })();
    }, [machineId, readyToLoad])
  );

  const productionBatchId = machineStatus?.productionBatchId;

  const jobIds = batch?.data?.links?.map((b) => b.productionOperationId) ?? [];

  useFocusEffect(
    useCallback(() => {
      if (!readyToLoad) return;
      if (productionBatchId == null) return;
      loadSingleBatch(productionBatchId);
    }, [productionBatchId, readyToLoad])
  );

  useFocusEffect(
    useCallback(() => {
      if (!readyToLoad) return;
      if (productionBatchId == null || !jobIds.length) return;

      (async () => {
        const jobsData = await operationsSearch({
          data: {},
          filter: { oDataFilter: `ProductionOperationId eq ${jobIds.join(' or ProductionOperationId eq ')}`, orderBy: ['ModifiedOn desc'] },
          select: null,
          resetHistory: true,
          ignoreHistory: true,
        });
        console.log(jobsData, 'jobsData');
        setJobs(jobsData.searchResult);
      })();
    }, [jobIds?.length, productionBatchId, readyToLoad])
  );

  const machineConnector = connector?.connectorDetails?.status?.find((c: any) => c.workerType === workerType.MACHINES);

  const connectorLoading = !connector.loaded;
  const noConnector = subStatus === subStatuses.NoConnector;
  const connectorOffline = subStatus === subStatuses.ConnectorOff;
  const connectionSwitchedOff = subStatus === subStatuses.ConnectionOff;
  const notProductive = status === machineStatuses.NOTPRODUCTIVE && !machineStatus.ncFile;

  const shouldDisplayUnavailableContentScreen =
    connectorLoading || noConnector || connectorOffline || machineOffline || notProductive || connectionSwitchedOff;

  const insetBottom = !isWeb && safeAreaInsetsBottom ? 10 : 0;
  const insetTop = !isWeb && safeAreaInsetsTop ? 20 : 0;

  return (
    <TourTarget tourId={OPERATOR_PANEL_TOUR_IDS.MAIN_CONTAINER} style={{ height: isWeb ? '100vh' : '100%', width: '100%', overflow: 'hidden' }}>
      {!shouldDisplayUnavailableContentScreen && (
        <View style={{ width: '100%', flex: 1 }}>
          <GridBox
            key="mainComponent"
            {...{
              horizontalLine: 5,
              verticalsLine: 4,
              horizontalSpace: 2,
              verticalSpace: 9,
              horizontalEdgeBottom: 0,
              horizontalEdgeTop: 0,
              verticalEdge: 0,
              backgroundColor: bgColor,
              customHeight: fullHeight,
              customWidth: fullWidth,
              useScaleWeb: false,
              useScaleOP: true,
            }}
          >
            {showEnterCount ? (
              <EnterCount
                {...{
                  ...props,
                  CC,
                  bgColor,
                  count,
                  setCount,
                  setShowEnterCount,
                  scale: scaleOP,
                }}
              />
            ) : (
              <Panel
                {...{
                  ...props,
                  CC,
                  bgColor,
                  count,
                  correctQuantity,
                  setCount,
                  showEnterCount,
                  setShowEnterCount,
                  maxCount: !!batch?.data?.links?.length ? maxCount : null,
                  isOperator,
                  productionBatchId: batch.data?.productionBatchId,
                  insetBottom,
                  insetTop,
                  scale: scaleOP,
                  font: fontOP,
                }}
              />
            )}
          </GridBox>
        </View>
      )}

      {shouldDisplayUnavailableContentScreen && (
        <UnavailableContentComponents
          {...{
            CC,
            connectorLoading,
            connectorOffline,
            connectionSwitchedOff,
            lastAliveEventReceivedOn: machineConnector?.lastAliveEventReceivedOn,
            lastUpdatedOn: machineStatus?.lastUpdatedOn,
            machineName: selectedItem.fullName,
            machineOffline,
            machineStatus: status,
            noConnector,
            notProductive,
            subStatus,
            isOperator,
            insetTop,
            insetBottom,
            scale: scaleOP,
            font: fontOP,
          }}
        />
      )}
    </TourTarget>
  );
};

const mapStateToProps = ({
  machines: { selectedItem, machinesLoaded, machines },
  clampingTools: { clampingTools },
  batch: { batch },
  connector,
}) => ({
  selectedItem,
  clampingTools,
  batch,
  connector,
  machinesLoaded,
  machines,
});

export default connect(mapStateToProps, { setSelectedMachine, getMachines, loadSingleBatch, operationsSearch, setJobs, correctQuantity })(
  ColorsWrapper(OperatorPanel)
);
